import { create } from 'zustand';
import type { Bounds, ConnectionId, ObjectId, Selection } from '@/types';

export interface SelectionStore {
  readonly selection: Selection;
  /** Live marquee rectangle in world space, or null when not marquee-selecting. */
  readonly marquee: Bounds | null;
  readonly select: (objectIds: readonly ObjectId[], additive?: boolean) => void;
  /** Selects connections; object selection is cleared unless additive. */
  readonly selectConnections: (connectionIds: readonly ConnectionId[], additive?: boolean) => void;
  readonly toggle: (objectId: ObjectId) => void;
  readonly deselect: (objectIds: readonly ObjectId[]) => void;
  /** Selects every id in `all`. */
  readonly selectAll: (all: readonly ObjectId[]) => void;
  /** Selected ⇄ unselected within `all`. */
  readonly invert: (all: readonly ObjectId[]) => void;
  readonly clear: () => void;
  readonly setMarquee: (bounds: Bounds | null) => void;
  /** Drops ids that no longer exist in the document. */
  readonly prune: (existing: ReadonlySet<ObjectId>, existingConnections?: ReadonlySet<ConnectionId>) => void;
}

const EMPTY: Selection = { objectIds: [], connectionIds: [] };

const sameIds = (a: readonly ObjectId[], b: readonly ObjectId[]): boolean =>
  a.length === b.length && a.every((id, i) => b[i] === id);

export const useSelectionStore = create<SelectionStore>()((set, get) => {
  const setIds = (objectIds: readonly ObjectId[]) => {
    set((s) => (sameIds(s.selection.objectIds, objectIds) ? s : { selection: { ...s.selection, objectIds } }));
  };
  return {
    selection: EMPTY,
    marquee: null,
    select: (objectIds, additive = false) => {
      const current = get().selection.objectIds;
      const next = additive ? [...new Set([...current, ...objectIds])] : [...objectIds];
      set((s) =>
        sameIds(s.selection.objectIds, next) && (additive || s.selection.connectionIds.length === 0)
          ? s
          : { selection: { objectIds: next, connectionIds: additive ? s.selection.connectionIds : [] } },
      );
    },
    selectConnections: (connectionIds, additive = false) => {
      set((s) => {
        const next = additive ? [...new Set([...s.selection.connectionIds, ...connectionIds])] : [...connectionIds];
        const objectIds = additive ? s.selection.objectIds : [];
        return { selection: { objectIds, connectionIds: next } };
      });
    },
    toggle: (objectId) => {
      const ids = get().selection.objectIds;
      setIds(ids.includes(objectId) ? ids.filter((x) => x !== objectId) : [...ids, objectId]);
    },
    deselect: (objectIds) => {
      const drop = new Set(objectIds);
      setIds(get().selection.objectIds.filter((id) => !drop.has(id)));
    },
    selectAll: (all) => {
      setIds([...all]);
    },
    invert: (all) => {
      const current = new Set(get().selection.objectIds);
      setIds(all.filter((id) => !current.has(id)));
    },
    clear: () => {
      set({ selection: EMPTY, marquee: null });
    },
    setMarquee: (marquee) => {
      set({ marquee });
    },
    prune: (existing, existingConnections) => {
      set((s) => {
        const objectIds = s.selection.objectIds.filter((id) => existing.has(id));
        const connectionIds = existingConnections
          ? s.selection.connectionIds.filter((id) => existingConnections.has(id))
          : s.selection.connectionIds;
        return objectIds.length === s.selection.objectIds.length &&
          connectionIds.length === s.selection.connectionIds.length
          ? s
          : { selection: { objectIds, connectionIds } };
      });
    },
  };
});
